import { ZONES, FLAT_FEES, ADD_ONS } from "@/lib/business";
import { AlertCircle } from "lucide-react";

export const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="section-pad bg-surface">
    <div className="container-tn">
      <div className="max-w-3xl">
        <span className="eyebrow">Transparent Pricing</span>
        <span className="gold-rule" />
        <h2 className="font-serif text-navy uppercase text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
          Know the cost<br />before I arrive.
        </h2>
        <p className="mt-5 text-mutedink text-base sm:text-lg leading-relaxed">
          Your visit total is a travel/convenience fee based on distance{" "}
          <strong className="text-navy font-semibold">plus</strong> the Illinois statutory notary fee
          of <strong className="text-navy font-semibold">$5 per notarized signature</strong>. Each
          additional signature is +$5.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Travel zone fees */}
        <div className="lg:col-span-2 card-tn !p-0 overflow-hidden">
          <div className="bg-navy px-6 py-4">
            <h3 className="font-serif text-white uppercase text-lg tracking-[0.15em] font-semibold">
              Travel Zone Visit Fees
            </h3>
            <p className="text-gold-soft text-xs mt-1 tracking-wide">
              Includes travel + one $5 statutory act · Additional signatures +$5
            </p>
          </div>
          <ul className="divide-y divide-gold-soft/40">
            {ZONES.map((z) => (
              <li
                key={z.key}
                data-testid={`price-zone-${z.key}`}
                className="flex items-center justify-between px-6 py-4"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <span className="flex items-center justify-center w-10 h-10 rounded-md bg-navy/[0.06] text-navy font-serif font-bold border border-gold-soft/60">
                    {z.key}
                  </span>
                  <div className="min-w-0">
                    <p className="font-sans font-semibold text-ink">Zone {z.key}</p>
                    <p className="text-xs text-mutedink">{z.range}</p>
                  </div>
                </div>
                <p className="font-serif text-navy text-xl sm:text-2xl font-bold">${z.fee}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* Add-ons */}
        <div className="card-tn !p-0 overflow-hidden">
          <div className="bg-navy px-6 py-4">
            <h3 className="font-serif text-white uppercase text-lg tracking-[0.15em] font-semibold">
              Add-ons
            </h3>
            <p className="text-gold-soft text-xs mt-1 tracking-wide">Applied when applicable</p>
          </div>
          <ul className="divide-y divide-gold-soft/40">
            {ADD_ONS.map((a) => (
              <li
                key={a.label}
                data-testid={`price-addon-${a.label.replace(/\s+/g, "-").toLowerCase()}`}
                className="flex items-center justify-between px-6 py-4"
              >
                <span className="text-ink text-sm sm:text-base">{a.label}</span>
                <span className="font-serif text-navy font-bold">{a.price}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Flat-fee services */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {FLAT_FEES.map((f) => (
          <div
            key={f.title}
            data-testid={`flat-fee-${f.title.replace(/\s+/g, "-").toLowerCase()}`}
            className="card-tn"
          >
            <p className="font-serif text-gold text-2xl font-bold">{f.price}</p>
            <h4 className="mt-2 font-serif text-navy uppercase text-sm tracking-[0.12em] font-semibold">
              {f.title}
            </h4>
            <p className="mt-2 text-sm text-mutedink leading-relaxed">{f.detail}</p>
          </div>
        ))}
      </div>

      {/* IL statutory notice */}
      <div
        data-testid="il-statutory-notice"
        className="mt-10 bg-navy border border-gold/60 rounded-md p-6 sm:p-8 flex flex-col sm:flex-row gap-5 items-start"
      >
        <AlertCircle className="w-8 h-8 text-gold shrink-0" strokeWidth={2} />
        <div>
          <h4 className="font-serif text-gold uppercase tracking-[0.2em] text-sm font-bold">
            Illinois Statutory Notice
          </h4>
          <p className="mt-3 text-white/90 text-sm sm:text-base leading-relaxed">
            All travel, mobile, evening, weekend, and rush charges are service fees for coming to you
            and the time involved — not charges for the notarial act itself, which is capped at{" "}
            <strong className="text-gold-soft">$5 per signature</strong> under Illinois law. Final
            price confirmed before every appointment.
          </p>
        </div>
      </div>
    </div>
  </section>
);
